import { noop } from "lodash-es";
import type { ConnectionOptions, NatsConnection, Status } from "nats.ws";
import { connect, DebugEvents, ErrorCode, Events, NatsError } from "nats.ws";

import { ApplicationError } from "@/error/ApplicationError";
import { reload } from "@/error/reload";
import { captureException, captureMessage } from "@/error/sentry";
import type { LogoutReason } from "@/router/types";
import { useBoardsStore } from "@/store/boards";
import { useConnectionStore } from "@/store/connection";
import { useLoadingStore } from "@/store/loading";
import { useStatisticsStore } from "@/store/statistics";
import { useUserStore } from "@/store/user";
import { isBrowserTabActive } from "@/utils/dom/dom";
import { natsUrl } from "@/utils/env/Environment";
import { isNonPrimitive } from "@/utils/type";

import { refreshToken } from "./http/api.config";
import { HttpRpcBackend } from "./impl/HttpRpcBackend";
import { NatsBackendSession } from "./impl/NatsBackendSession";
import { NatsRpcBackend } from "./impl/NatsRpcBackend";
import { SwitchingRpcBackend } from "./impl/SwitchingRpcBackend";
import { saveBackendSession } from "./impl/backendSessionCache";
import { lastError } from "./natsInternal";
import { closeSession, initBackendReader, reconnect } from "./read/BackendReader";
import { writer } from "./write/BackendWriter";

const connectRetries = 1;
const httpCallPaths = ["link.list"];

export type Connection = NatsConnection & { onError: (msg: string) => void };

let currentConnection: Connection | undefined;

interface CloseHandler {
  login: () => void | Promise<unknown>;
  logout: (reason: LogoutReason) => void | Promise<unknown>;
}

export async function openConnection(
  currentPath: () => string,
  onClose: CloseHandler,
  connectionName = "",
): Promise<void> {
  const doRefreshToken = async () => {
    try {
      await refreshToken({ path: "#" + currentPath() });
      reload("refresh-token");
    } catch (_) {
      void onClose.logout("refresh-failed");
    }
  };
  currentConnection = await doConnect(
    {
      servers: [natsUrl],
      pingInterval: 4500,
      maxPingOut: 1,
      maxReconnectAttempts: -1,
      name: connectionName,
      waitOnFirstConnect: true,
    },
    doRefreshToken,
  );
  if (currentConnection) {
    void addConnectionStateHandlers(currentConnection);
    void addCloseConnectionHandlers(currentConnection, doRefreshToken, onClose.logout);
    await initNatsSession(currentConnection);
    useConnectionStore().netWs = true;
    if (useBoardsStore().boardsInited) {
      // it's a reconnect
      reconnect();
    }
  }
}

async function initNatsSession(connection: Connection) {
  const session = new NatsBackendSession(
    connection,
    useUserStore().technicalUser,
    new SwitchingRpcBackend(
      [
        new NatsRpcBackend(
          connection,
          useUserStore().technicalUser.company,
          useUserStore().technicalUser.id,
        ),
        new HttpRpcBackend(),
      ],
      (path) => (useHttp(path) ? 1 : 0),
    ),
  );
  await useLoadingStore().load("initBackend", initReaderAndWriter(session));
  return session;
}

function useHttp(path: string) {
  return httpCallPaths.includes(path);
}

async function initReaderAndWriter(session: NatsBackendSession) {
  saveBackendSession(session);
  await initBackendReader(session);
  writer.initSession(session);
}

async function doConnect(
  options: ConnectionOptions,
  refreshToken: () => Promise<void>,
  retries = 0,
) {
  const start = Date.now();
  try {
    return Object.assign(await connect(options), { onError: noop });
  } catch (error) {
    if (shouldRefreshToken(error)) {
      await refreshToken();
      return;
    }
    if (shouldReload(error)) {
      if (retries < connectRetries) {
        return doConnect(options, refreshToken, retries + 1);
      }
      captureException(error, { request: { timeout: Date.now() - start } });
      // TODO show a message somehow?
      throw error;
    }
    if (shouldLogout(error)) {
      // TODO show a message somehow?
      throw error;
    }
    captureException(error);
    throw error;
  }
}

export async function closeConnection() {
  try {
    if (currentConnection) {
      const open = !currentConnection.isClosed();
      closeSession(open);
      if (open) {
        await currentConnection.close();
      }
    } else {
      closeSession();
    }
  } catch (e) {
    captureException(e);
  }
}

async function addConnectionStateHandlers(connection: Connection) {
  for await (const status of connection.status()) {
    try {
      handleStatus(connection, status);
    } catch (e) {
      statusError(e);
    }
  }
}

function handleStatus(connection: Connection, status: Status) {
  if (status.type !== DebugEvents.PingTimer) {
    useConnectionStore().addWebsocketState(status.type);
  }
  switch (status.type) {
    case Events.Disconnect:
    case DebugEvents.StaleConnection:
      useConnectionStore().disconnectWebsocket();
      break;
    case Events.Reconnect:
      useStatisticsStore().current.reconnects++;
      useConnectionStore().reconnectWebsocket();
      break;
    case Events.Error: {
      const message = lastError(connection)?.message;
      switch (message) {
        case NATS_ERRORS.AUTH_VIOLATION:
        case NATS_ERRORS.AUTH_EXPIRED:
        case NATS_ERRORS.AUTH_REVOKED:
          // triggers a connection.close and then a token refresh and page reload. don't log
          break;
        case NATS_ERRORS.STALE_CONNECTION:
          // Server-side ping timeout after 2x2min (?), triggers a connection.close and page reload
          // still curious: what is the browser doing in these 4min?
          if (isBrowserTabActive()) {
            connection.onError("Server-side ping timeout");
          }
          break;
        case NATS_ERRORS.AUTH_TIMEOUT:
          // Needs more investigation... keep logging for now
          if (isBrowserTabActive()) {
            connection.onError(message);
          }
          break;
        default:
          // sometimes lastError not populated, but status.data is :shrug:
          if (status.data === ErrorCode.AuthenticationExpired) {
            return;
          }
          connection.onError(
            message ?? (isNonPrimitive(status.data) ? "unknown" : String(status.data)),
          );
      }
      break;
    }
  }
}

function statusError(e: unknown) {
  try {
    captureException(e);
  } catch (_) {
    // nothing more we can do
  }
}

async function addCloseConnectionHandlers(
  connection: Connection,
  refreshToken: () => Promise<unknown>,
  logout: (reason: LogoutReason) => void | Promise<unknown>,
) {
  const clientId = connection.info?.client_id;
  const error = await connection.closed();
  if (currentConnection === connection) {
    await closeConnection();
  }
  if (!error) {
    return;
  }
  if (shouldRefreshToken(error)) {
    await refreshToken();
    return;
  }
  if (shouldReload(error)) {
    return reload("connection-closed");
  }
  if (shouldLogout(error)) {
    void logout("connection-refused");
    return;
  }
  captureMessage(`Unhandled connection error: ${error.message}`, {
    error: { clientId, info: connection.info, error },
  });
  if (error instanceof NatsError && error.chainedError) {
    captureException(error.chainedError);
  }
  void logout("unknown");
}

function shouldRefreshToken(error: unknown) {
  if (error instanceof NatsError) {
    const refreshErrors = [
      ErrorCode.AuthenticationExpired,
      ErrorCode.AuthorizationViolation,
      ErrorCode.Disconnect,
    ].map((err) => err.valueOf());
    if (refreshErrors.includes(error.code)) {
      return true;
    }

    const refreshServerErrors = [NATS_ERRORS.AUTH_REVOKED];
    if (error.isProtocolError() && refreshServerErrors.includes(error.message)) {
      return true;
    }
  }
}

function shouldReload(error: unknown) {
  if (error instanceof NatsError) {
    const reloadServerErrors = [NATS_ERRORS.AUTH_TIMEOUT, NATS_ERRORS.STALE_CONNECTION];
    if (error.isProtocolError() && reloadServerErrors.includes(error.message)) {
      return true;
    }
  }
  if (unexpectedServerMessage(error)?.startsWith("-ERR 'Authentication Timeout'")) {
    return true;
  }
}

function shouldLogout(error: unknown) {
  if (error instanceof NatsError) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    return error.code === ErrorCode.ConnectionRefused;
  }
}

function unexpectedServerMessage(error: unknown): string | undefined {
  if (error instanceof ApplicationError) {
    if (error.message === "unexpected response from server") {
      return error.contexts.response?.data?.toString();
    }
  }
}

const NATS_ERRORS = {
  AUTH_VIOLATION: "'Authorization Violation'",
  AUTH_EXPIRED: "'User Authentication Expired'",
  AUTH_REVOKED: "'User Authentication Revoked'",
  AUTH_TIMEOUT: "'Authentication Timeout'",
  STALE_CONNECTION: "'Stale Connection'",
};
